/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "i18n/i18n"; // Initialize i18n
import { queryClientOptions } from "lib/utils/reactQueryConfig";
import { StrictMode } from "react";
import { CookiesProvider } from "react-cookie";
import { createRoot } from "react-dom/client";
import { Toaster } from "react-hot-toast";
import { BrowserRouter } from "react-router-dom";
import { SafeComponent } from "routes/RouterManagers/RouteManager";
import { EnvManager } from "state/EnvironmentsContext/EnvManager";
import FeatureFlagManager from "state/featureFlags/FeatureFlagManager";
import { GeneralProvider } from "state/GeneralContext";
import { InvitationProvider } from "state/InvitationContext";
import { PrefNavigatorProvider } from "state/PrefNavigatorContext";
import { UserProvider } from "state/UserContext";
import { sentryCaptureError } from "utils/errorFormatter";
import warning from "utils/warning";
import "./index.css";
import Layout from "./Layout";
import { AuthProvider, OrgDataProvider, UIContextProvider } from "./state";
import { AppThemingProvider } from "state/AppThemingContext";

export const queryClient = new QueryClient(queryClientOptions);

// Global error handlers for unhandled errors and promise rejections
window.addEventListener("error", (event) => {
  event.preventDefault();
  sentryCaptureError(event.error, "Global Error Handler");
  warning(event.error);
});

const container = document.getElementById("root");
const root = createRoot(container!);

root.render(
  <StrictMode>
    <BrowserRouter>
      <SafeComponent>
        <QueryClientProvider client={queryClient}>
          <OrgDataProvider>
            <GeneralProvider>
              <AuthProvider>
                <EnvManager>
                  <FeatureFlagManager>
                    <AppThemingProvider>
                      <CookiesProvider>
                        <UIContextProvider>
                          <UserProvider>
                            <InvitationProvider>
                              <PrefNavigatorProvider>
                                <Layout />
                                <Toaster
                                  toastOptions={{
                                    duration: 2500,
                                    style: {
                                      display: "inline-flex",
                                      padding: "20px",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      gap: "10px",
                                      borderRadius: "20px",
                                      backgroundColor:
                                        "var(--theme-colors-neutral-card-bg)",
                                      color:
                                        "var(--theme-colors-neutral-card-fg)",
                                      fontSize: "14px",
                                    },
                                    success: {
                                      style: {
                                        backgroundColor:
                                          "var(--theme-colors-success-card-bg)",
                                        color:
                                          "var(--theme-colors-success-card-fg)",
                                      },
                                    },
                                    loading: {
                                      style: {
                                        backgroundColor:
                                          "var(--theme-colors-neutral-card-bg)",
                                        color:
                                          "var(--theme-colors-neutral-card-fg)",
                                      },
                                    },
                                    error: {
                                      style: {
                                        backgroundColor:
                                          "var(--theme-colors-danger-card-bg)",
                                        color:
                                          "var(--theme-colors-danger-card-fg)",
                                      },
                                    },
                                  }}
                                />
                              </PrefNavigatorProvider>
                            </InvitationProvider>
                          </UserProvider>
                        </UIContextProvider>
                      </CookiesProvider>
                    </AppThemingProvider>
                  </FeatureFlagManager>
                </EnvManager>
              </AuthProvider>
            </GeneralProvider>
          </OrgDataProvider>
        </QueryClientProvider>
      </SafeComponent>
    </BrowserRouter>
  </StrictMode>,
);
